import { c, msgid, ngettext } from 'ttag';

import { MembershipDisplayStatus } from '../../graphql/__generated__/schema';
import { PortalUserQuery } from '../../graphql/__generated__/types';

type PortalUser = NonNullable<PortalUserQuery['portalUser']>;

/**
 * Where an invitation stands, and whether resending it is a gesture that can work.
 *
 * **Attention, the three situations the sheet used to flatten.** `invitedAt`,
 * `invitationExpiresAt` and `invitationSentCount` were all read and all printed as
 * bare dates in a grid of fields, which says « invitée le 12/07, valide jusqu'au
 * 19/07, 3 envois » and leaves the operator to work out that it expired four days
 * ago and has already been chased twice. An invitation expiring in two days, one
 * resent three times and one expired last week call for three different reactions,
 * so they get three different sentences. **The button only makes sense if the state
 * it repairs is legible.**
 */
export type InvitationState =
    /** Nothing to say: the person is in, or holds no pending access. */
    | { kind: 'none' }
    /**
     * Invited, with **no link at all**: not an expired one, none.
     *
     * Attention, this is what reopening an access produces (PCG-94), and it is the state the
     * sheet used to render as `none`, showing no invitation card and no button. The
     * revocation had cancelled the pending invitation when it closed the person's
     * last access, and `reinstateMembership` sends no mail and creates no invitation:
     * the person comes back « invitée » with nothing to activate. Saying so is the
     * only thing that gets the operator to finish the gesture.
     */
    | { kind: 'missing'; sentCount: number }
    /** Live, with room to spare. */
    | { kind: 'pending'; days: number; sentCount: number }
    /** Live, but about to die: the case where a resend is anticipation, not repair. */
    | { kind: 'expiring'; days: number; sentCount: number }
    /** Dead. Only a resend brings the person back a usable link. */
    | { kind: 'expired'; days: number; sentCount: number };

/** Below this, an invitation is worth flagging rather than merely dating. */
const EXPIRING_SOON_DAYS = 3;

const wholeDaysBetween = (from: Date, to: Date): number =>
    Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));

/**
 * Whether an invitation would lead anywhere: at least one access still waiting.
 *
 * Machine A is not accepted at this point, so `active` cannot occur: no membership
 * left in `invited` means every access has been closed, which is the state the API
 * refuses with `INVITATION_NOT_FOUND`.
 */
const hasPendingAccess = (user: PortalUser): boolean =>
    (user.memberships ?? []).some(
        (membership) =>
            membership?.status === MembershipDisplayStatus.Invited ||
            membership?.status === MembershipDisplayStatus.InvitationExpired
    );

export const invitationStateOf = (user: PortalUser, now: Date = new Date()): InvitationState => {
    // An activated account has no invitation left to speak of, whatever dates the
    // row still carries.
    if (user.activatedAt) {
        return { kind: 'none' };
    }
    // No live link, and the two situations behind that are not the same news: an
    // access still waiting means the link is **missing**, anything else means there
    // is nothing an invitation would lead to.
    if (!user.invitationExpiresAt) {
        return hasPendingAccess(user)
            ? { kind: 'missing', sentCount: user.invitationSentCount ?? 0 }
            : { kind: 'none' };
    }

    const sentCount = user.invitationSentCount ?? 1;
    const expiry = new Date(user.invitationExpiresAt);
    const days = wholeDaysBetween(now, expiry);

    if (days < 0) {
        return { kind: 'expired', days: Math.abs(days), sentCount };
    }
    return { kind: days <= EXPIRING_SOON_DAYS ? 'expiring' : 'pending', days, sentCount };
};

/**
 * The sentence that states the situation. Written here rather than in the component
 * so the three readings sit side by side and cannot drift apart.
 */
export const invitationSentence = (state: InvitationState): string | null => {
    switch (state.kind) {
        case 'missing':
            // Attention, not « expirée » : nothing expired, there is no link. Said as the
            // unfinished half of a gesture, because that is what it is.
            return c('Invitation state')
                .t`This person is invited but holds no activation link: reopening an access does not create one, and the closure had cancelled the one that was pending. Nothing will reach them until the invitation is sent again.`;
        case 'expired':
            return ngettext(
                msgid`This invitation expired ${state.days} day ago: the link it carried no longer works.`,
                `This invitation expired ${state.days} days ago: the link it carried no longer works.`,
                state.days
            );
        case 'expiring':
            // Zero is « today », and « expires in 0 days » is not a sentence anybody
            // reads correctly.
            if (state.days === 0) {
                return c('Invitation state').t`This invitation expires today.`;
            }
            return ngettext(
                msgid`This invitation expires in ${state.days} day.`,
                `This invitation expires in ${state.days} days.`,
                state.days
            );
        case 'pending':
            return ngettext(
                msgid`This invitation is valid for another ${state.days} day.`,
                `This invitation is valid for another ${state.days} days.`,
                state.days
            );
        default:
            return null;
    }
};

/**
 * How many times it has been sent, said only when it is more than once.
 *
 * A first send is the normal course and needs no comment; a second and a third say
 * something: the address may be wrong, and chasing it a fourth time will not fix
 * that. This is the whole reason `invitationSentCount` is on the sheet.
 */
export const invitationChaseSentence = (state: InvitationState): string | null => {
    if (state.kind === 'none' || state.sentCount <= 1) {
        return null;
    }
    const count = state.sentCount;
    return ngettext(
        msgid`It has already been sent ${count} time. If it keeps going unanswered, check the address before sending again.`,
        `It has already been sent ${count} times. If it keeps going unanswered, check the address before sending again.`,
        count
    );
};

/**
 * Whether « relancer » can work, and why not when it cannot.
 *
 * **Attention, disabled with its reason, never removed**: the house rule. The two cases
 * below are **states of the data**, which the sheet can read for itself, so refusing
 * them here spares a round trip that would have refused anyway.
 *
 * Attention, and only those two. `MISSING_PERMISSION` and `FORBIDDEN_OUT_OF_SCOPE` are NOT
 * pre-empted: `viewer` exposes the role but not the permissions behind it, so no
 * screen can know whether the actor holds `manage_users` on this person's accounts.
 * Those refusals are rendered **after** the click, naming the right that is missing:
 * the one place the « disabled with its reason » rule cannot be honoured.
 */
export const resendBlocker = (user: PortalUser): string | null => {
    if (user.activatedAt) {
        return c('Invitation blocker')
            .t`This person has already activated their account: there is no invitation left to send. They sign in with their password.`;
    }
    if (user.disabledAt) {
        // Attention, it used to read « rouvrez d'abord l'accès », which now names a gesture
        // this sheet really performs, and the wrong one: what a disabled person
        // needs is their account reactivated, and reopening an access would change
        // nothing for them.
        return c('Invitation blocker')
            .t`This person's account is disabled. Reactivating it comes first: an invitation to a disabled person leads nowhere, whatever their accesses say.`;
    }
    // No access left in waiting means every one has been closed, and the API refuses
    // with INVITATION_NOT_FOUND. Reading it here turns a server refusal into a
    // legible state, and names the gesture that repairs it, which now exists.
    if ((user.memberships ?? []).length > 0 && !hasPendingAccess(user)) {
        return c('Invitation blocker')
            .t`This person holds no pending access. Reopen a membership first: there is nothing to invite them to.`;
    }
    return null;
};
